import Ad from "@components/Advertisement/Ad";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import { GADataProps } from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import {
  BT_NEWSLETTERS,
  NEWSLETTER_TYPE,
} from "@components/Newsletter/types/Newsletter";
import NewsletterSignUpPanel from "@components/Newsletter/Verticals/NewsletterSignUp";
import VerticalBlockTitle from "@components/VerticalBlockTitle/VerticalBlockTitle";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { getBreadcrumbProperties } from "@features/seo/helpers/getJsonLdProperties";
import { useInjectClientData } from "@hooks/useInjectClientData";
import { CardVariations } from "@particles/card/utils/types";
import Listing from "@particles/listing/Listing";
import { getUrlPathFromAbsoluteUrl } from "@routes/Article/utils/helpers";
import VerticalBannerFactory from "@routes/Section/layouts/Verticals/components/VerticalBannerFactory";
import SMENewsletterDescription from "@routes/Section/layouts/Verticals/SME/components/SMENewsletterDescription";
import { VerticalsLatestSectionPageData } from "@routes/Section/utils/types";
import { USFBreadcrumbSchema } from "@sphtech/web2-seo-schema";
import { cn } from "@util/helpers";

/** The vertical latest page content. */
const VerticalsLatestPageContent = ({
  articles,
  vertical,
  entity,
  seoFooterData,
  gaProps,
}: VerticalsLatestSectionPageData & {
  gaProps: GADataProps;
}): React.ReactElement => {
  const relativePath = getUrlPathFromAbsoluteUrl(entity.urlPath);

  const variant: CardVariations = {
    image: {
      position: "right",
      width: "w-4/10 md:w-2/10 lg:w-3/20",
      extraClass: "order-3",
    },
    content: {
      width: "w-6/10 md:w-8/10 lg:17/20",
      extraClass: "pr-4",
      blurb: {
        extraClass: "!font-poppins",
      },
      updatedTimeExtraClass:
        "truncate uppercase text-gray-550 font-poppins text-7xs",
      updatedTimeWrapperClass: "mt-auto",
    },
    kicker: {
      color: "text-gray-515",
      extraClass: "font-poppins text-4xs",
    },
    title: {
      color: "text-gray-850",
      size: "text-base",
      extraClass: cn("font-lct font-semibold leading-150"),
    },
  };

  const breadcrumbList = getBreadcrumbProperties(entity);
  useInjectClientData({ gaProps });

  return (
    <div className="w-full" data-testid="verticals-latest-page-content">
      <USFBreadcrumbSchema items={breadcrumbList} />

      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header />

      <PulseEntry />

      <Container rootClassName="py-4">
        <VerticalBannerFactory vertical={vertical} className="my-6" />

        <VerticalBlockTitle rootClassName="mb-4" text="Latest" />

        <Listing
          cardProps={articles}
          urlPath={relativePath}
          pageType={"section"}
          size={10}
          templateGCardVariantOverride={variant}
          sections={entity.uniqueName}
        />
      </Container>

      <NewsletterSignUpPanel
        rootClassName="my-8"
        descriptionComponent={<SMENewsletterDescription />}
        newsletter={BT_NEWSLETTERS.filter(
          (newsletter) => newsletter.type === NEWSLETTER_TYPE.SGSME
        )}
        btnColors="bg-verticals-sme text-white hover:bg-white hover:text-verticals-sme"
        linkColors="text-verticals-sme"
      />

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />
      <Ad adType="prestitial" />
    </div>
  );
};

export default VerticalsLatestPageContent;
